import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { AdjustIcon, BlurIcon, ReplaceIcon } from '../../media/optimize-icons';

import { useStyles } from './style';
import { ViewName } from '../../model/utils';

export const OptimizeView = ({
  i18n, resultImage, setCurrentView, updateAdPlacementImage, goToImageAdjustView, goToBackgroundChangeView, isAdjustButtonDisabled,
}) => {
  const optimizeViewStyles = useStyles();

  const handleAdPlacementClick = useCallback(() => {
    setCurrentView(ViewName.PlacementListView);
    updateAdPlacementImage();
  }, [setCurrentView, updateAdPlacementImage]);

  const recommendationList = useMemo(() => {
    const placementList = [
      {
        className: 'adjust-crop',
        title: _TL_('Adjust crop for each size'),
        description: _TL_('Customize how the image is cropped for every aspect ratio and see how it looks in the ad'),
        icon: <AdjustIcon />,
        onClick: handleAdPlacementClick,
      },
    ];
    const adjustList = [
      {
        className: 'blur-background',
        title: _TL_('Blur the background'),
        description: _TL_('Add blur to the background to simulate camera focus'),
        icon: <BlurIcon />,
        onClick: goToImageAdjustView,
      },
      {
        className: 'replace-background',
        title: _TL_('Replace the background'),
        description: _TL_('Generate a new background, use an existing image, or replace with a simple color'),
        icon: <ReplaceIcon />,
        onClick: goToBackgroundChangeView,
      },
    ];
    if (isAdjustButtonDisabled) {
      return placementList;
    }
    return [...placementList, ...adjustList];
  }, [goToBackgroundChangeView, goToImageAdjustView, handleAdPlacementClick, isAdjustButtonDisabled]);

  const renderRecommendationItem = useCallback(({
    title, description, icon, onClick, className,
  }, index) => (
    <div
      key={`${index}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      className={classnames('recommendation-item', className)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          onClick();
        }
      }}
    >
      <div className="icon-box">{icon}</div>
      <div className="item-content">
        <div className="item-title">{i18n.getString(title)}</div>
        <div className="item-desc">{i18n.getString(description)}</div>
      </div>
    </div>
  ), [i18n]);

  return (
    <div className={classnames('optimize-view-container', optimizeViewStyles)}>
      <div className={classnames('recommendation-container')}>
        <div className="recommendation-title">{i18n.getString('Recommendations')}</div>
        <div className="recommendation-content">
          {recommendationList.map((item, index) => renderRecommendationItem(item, index))}
        </div>
      </div>
      <div className={classnames('main-image-container')}>
        <div className="image-wrapper">
          <img
            src={resultImage.url}
            alt="img preview"
          />
        </div>
      </div>
    </div>
  );
};

OptimizeView.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  resultImage: PropTypes.shape({
    url: PropTypes.string.isRequired,
  }).isRequired,
};
